import React, { useEffect, useState, useCallback } from "react";
import { useLang } from "../context/LanguageContext";
import { PageHeader } from "../components/common/PageHeader";
import { DataTable } from "../components/common/DataTable";
import { StatusBadge } from "../components/common/StatusBadge";
import { EntityDialog } from "../components/common/EntityDialog";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { CLFs } from "../lib/api";
import { formatIndianDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, Pencil, PowerOff, Power } from "lucide-react";

const clfFields = [
  { name: "name", label: "CLF name", required: true, placeholder: "e.g. Sarbajaya Matangini CLF" },
  { name: "registration_number", label: "Registration number", required: true, placeholder: "e.g. CLF-TRP-001", halfWidth: true },
  { name: "status", label: "Status", type: "select", halfWidth: true, options: [
    { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" },
  ] },
  { name: "district", label: "District", required: true, halfWidth: true, placeholder: "e.g. Gomati" },
  { name: "block", label: "Block", required: true, halfWidth: true, placeholder: "e.g. Udaipur" },
  { name: "contact_person", label: "Contact person", halfWidth: true },
  { name: "phone", label: "Phone", type: "tel", halfWidth: true, placeholder: "+91…" },
  { name: "address", label: "Address" },
];

const ClfsPage = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await CLFs.list({ include_inactive: includeInactive });
      setRows(data);
    } catch (e) {
      toast.error(e.message || "Failed to load CLFs");
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (vals) => {
    const payload = { ...vals };
    delete payload.status;
    await CLFs.create(payload);
    toast.success("CLF created");
    await load();
  };
  const handleUpdate = async (vals) => {
    await CLFs.update(editing.id, vals);
    toast.success("CLF updated");
    await load();
  };
  const toggleStatus = async (row) => {
    if (row.status === "active") {
      await CLFs.deactivate(row.id);
      toast.success(`${row.name} deactivated`);
    } else {
      await CLFs.activate(row.id);
      toast.success(`${row.name} activated`);
    }
    await load();
  };

  const columns = [
    { key: "registration_number", header: "Reg. No", mono: true, strong: true },
    { key: "name", header: "CLF name", strong: true },
    { key: "district", header: "District" },
    { key: "block", header: "Block" },
    { key: "contact_person", header: "Contact" },
    { key: "phone", header: "Phone", mono: true },
    { key: "created_at", header: "Created", mono: true, render: (r) => formatIndianDate(r.created_at) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status === "active" ? "Active" : "Closed"} /> },
    {
      key: "_actions", header: "Actions", render: (r) => (
        <div className="flex items-center gap-1.5">
          <Button
            size="sm" variant="outline"
            data-testid={`clf-edit-${r.id}`}
            onClick={() => { setEditing(r); setDialogOpen(true); }}
            className="h-8 px-2"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm" variant="outline"
            data-testid={`clf-toggle-${r.id}`}
            onClick={() => setConfirm({ row: r, action: r.status === "active" ? "deactivate" : "activate" })}
            className={`h-8 px-2 ${r.status === "active" ? "border-rose-300 text-rose-700 hover:bg-rose-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
          >
            {r.status === "active" ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title={t.nav.clfs}
        subtitle="Cluster Level Federations in your workspace"
        testId="clfs-page-header"
        showSampleBadge={false}
        actions={
          <>
            <label className="flex items-center gap-2 text-xs text-emerald-900/70">
              <Switch
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
                data-testid="toggle-include-inactive"
              />
              Show inactive
            </label>
            <Button
              data-testid="clf-add-btn"
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              className="bg-[#0F4C3A] text-white hover:bg-emerald-900"
            >
              <Plus className="mr-1.5 h-4 w-4" />Add CLF
            </Button>
          </>
        }
      />

      {loading ? (
        <div className="rounded-2xl border border-emerald-950/10 bg-white p-10 text-center text-sm text-emerald-900/50">
          Loading…
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          searchKeys={["name", "registration_number", "district", "block", "contact_person"]}
          testId="clfs-table"
          emptyLabel="No CLFs yet. Click “Add CLF” to create your first one."
        />
      )}

      <EntityDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? "Edit CLF" : "Register new CLF"}
        description={editing ? null : "Fill in the details below. Registration number must be unique."}
        fields={editing ? clfFields : clfFields.filter((f) => f.name !== "status")}
        initialValues={editing || { status: "active" }}
        onSubmit={editing ? handleUpdate : handleCreate}
        submitLabel={editing ? "Save changes" : "Create CLF"}
        testIdPrefix="clf-dialog"
      />

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.action === "deactivate" ? "Deactivate this CLF?" : "Reactivate this CLF?"}
        description={
          confirm?.action === "deactivate"
            ? `“${confirm?.row?.name}” will be hidden from active lists but kept in the database.`
            : `“${confirm?.row?.name}” will become active again.`
        }
        confirmLabel={confirm?.action === "deactivate" ? "Deactivate" : "Reactivate"}
        destructive={confirm?.action === "deactivate"}
        testId="clf-confirm"
        onConfirm={async () => { await toggleStatus(confirm.row); setConfirm(null); }}
      />
    </div>
  );
};

export default ClfsPage;
